import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { T, card, tbl, th, td } from '../lib/theme'

const fmt  = (n) => '₦' + (Number(n)||0).toLocaleString(undefined,{maximumFractionDigits:0})
const fmtDT = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '—'
  return d.toLocaleString()
}
const fmtD = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '—'
  return d.toLocaleDateString()
}

function enfRag(pct) {
  if (pct <= 25) return { color:'#f85149', bg:'rgba(248,81,73,.12)', border:'rgba(248,81,73,.25)', label:'At Risk'   }
  if (pct <= 50) return { color:'#E8A020', bg:'rgba(232,160,32,.15)',border:'rgba(232,160,32,.3)', label:'Watchlist' }
  return              { color:'#3fb950', bg:'rgba(63,185,80,.15)',  border:'rgba(63,185,80,.3)',  label:'Cleared'   }
}

function statusBadge(status) {
  if (status === 'closed')       return { color:'#3fb950', bg:'rgba(63,185,80,.12)',   border:'rgba(63,185,80,.3)',   label:'Closed'       }
  if (status === 'under_review') return { color:'#E8A020', bg:'rgba(232,160,32,.15)', border:'rgba(232,160,32,.3)', label:'Under Review' }
  return                                { color:'#f85149', bg:'rgba(248,81,73,.12)',   border:'rgba(248,81,73,.25)', label:'Open'         }
}

export default function Enforcement({ session, roleCtx }) {
  const role     = roleCtx?.role || 'guest'
  const canEdit  = role === 'admin' || role === 'compliance_officer'
  const canClose = role === 'admin'

  const [cases,       setCases]       = useState([])
  const [notes,       setNotes]       = useState({})
  const [loading,     setLoading]     = useState(true)
  const [expanded,    setExpanded]    = useState(null)
  const [newNote,     setNewNote]     = useState({})
  const [closingNote, setClosingNote] = useState({})
  const [saving,      setSaving]      = useState({})
  const [msg,         setMsg]         = useState(null)

  const [filterWeek,   setFilterWeek]   = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterSev,    setFilterSev]    = useState('')

  const selS = { fontSize:12, fontFamily:T.font, background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6, padding:'7px 12px',
    color:T.txtPrimary, cursor:'pointer' }

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('enforcement_cases')
      .select(`*, super_agents(id, company_name, lgas(name, tier))`)
      .order('week_ending', { ascending: false })
      .order('funding_pct', { ascending: true })
    if (!error) { const d = data || []; setCases(d); setFilterWeek(fw => fw === '' ? (d[0]?.week_ending || '') : fw) }
    setLoading(false)
  }, [])

  // Reload a single case from DB and update local state (avoids stale timestamps)
  async function refreshCase(caseId) {
    const { data } = await supabase
      .from('enforcement_cases')
      .select(`*, super_agents(id, company_name, lgas(name, tier))`)
      .eq('id', caseId)
      .single()
    if (data) setCases(prev => prev.map(c => c.id === caseId ? data : c))
  }

  useEffect(() => { load() }, [load])

  async function loadNotes(caseId) {
    const { data } = await supabase
      .from('case_notes')
      .select('*')
      .eq('case_id', caseId)
      .order('created_at', { ascending: true })
    setNotes(prev => ({ ...prev, [caseId]: data || [] }))
  }

  function toggleExpand(caseId) {
    if (expanded === caseId) { setExpanded(null) }
    else { setExpanded(caseId); loadNotes(caseId) }
  }

  // Add a note — only available while case is Open
  async function addNote(caseId) {
    const text = (newNote[caseId] || '').trim()
    if (!text) return
    setSaving(prev => ({ ...prev, [`note_${caseId}`]: true }))
    const { error } = await supabase.from('case_notes').insert({
      case_id:          caseId,
      note:             text,
      created_by:       session?.user?.id    || null,
      created_by_name:  session?.user?.user_metadata?.full_name || session?.user?.email || null,
      created_by_email: session?.user?.email || null,
    })
    if (!error) {
      setNewNote(prev => ({ ...prev, [caseId]: '' }))
      await loadNotes(caseId)
    }
    setSaving(prev => ({ ...prev, [`note_${caseId}`]: false }))
  }

  // Enforcement officer submits for admin approval
  async function submitForApproval(caseId) {
    setSaving(prev => ({ ...prev, [`submit_${caseId}`]: true }))
    const now = new Date().toISOString()
    const { error } = await supabase.from('enforcement_cases').update({
      status:            'under_review',
      reviewed_by:       session?.user?.id    || null,
      reviewed_by_name:  session?.user?.user_metadata?.full_name || session?.user?.email || null,
      reviewed_by_email: session?.user?.email || null,
      reviewed_at:       now,
      updated_at:        now,
    }).eq('id', caseId)
    if (!error) {
      await refreshCase(caseId)
      setMsg({ type:'ok', text:'Case submitted for admin approval.' })
    } else {
      setMsg({ type:'err', text: error.message })
    }
    setSaving(prev => ({ ...prev, [`submit_${caseId}`]: false }))
  }

  // Admin closes the case
  async function closeCase(caseId) {
    const cn = (closingNote[caseId] || '').trim()
    if (!cn) { setMsg({ type:'err', text:'A closing note is required to close a case.' }); return }
    setSaving(prev => ({ ...prev, [`close_${caseId}`]: true }))
    const now = new Date().toISOString()
    const { error } = await supabase.from('enforcement_cases').update({
      status:          'closed',
      closed_at:       now,
      closed_by:       session?.user?.id    || null,
      closed_by_name:  session?.user?.user_metadata?.full_name || session?.user?.email || null,
      closed_by_email: session?.user?.email || null,
      closing_note:    cn,
      updated_at:      now,
    }).eq('id', caseId)
    if (!error) {
      setClosingNote(prev => ({ ...prev, [caseId]: '' }))
      await refreshCase(caseId)
      setMsg({ type:'ok', text:'Case closed successfully.' })
    } else {
      setMsg({ type:'err', text: error.message })
    }
    setSaving(prev => ({ ...prev, [`close_${caseId}`]: false }))
  }

  const weeks    = [...new Set(cases.map(c => c.week_ending))].sort((a,b) => b.localeCompare(a))
  const filtered = cases.filter(c =>
    (!filterWeek   || c.week_ending === filterWeek) &&
    (!filterStatus || c.status      === filterStatus) &&
    (!filterSev    || c.severity    === filterSev)
  )
  const weekCases     = filterWeek ? cases.filter(c => c.week_ending === filterWeek) : cases
  const openCount      = weekCases.filter(c => c.status === 'open').length
  const reviewCount    = weekCases.filter(c => c.status === 'under_review').length
  const watchlistCount = weekCases.filter(c => c.severity === 'watchlist').length
  const atRiskCount    = weekCases.filter(c => c.severity === 'at_risk').length

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading enforcement cases…</div>

  return (
    <div>
      <div style={{ marginBottom:24, display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Performance Exceptions</div>
          <div style={{ fontSize:12, color:T.txtSecondary }}>
            Auto-generated from weekly scoring · Watchlist ≤50% · At Risk ≤25% · Admin sign-off required to close
          </div>
        </div>
        <button
          onClick={() => { setLoading(true); load() }}
          style={{ padding:'7px 16px', background:'rgba(30,144,255,.1)', color:T.blue,
            border:`1px solid rgba(30,144,255,.25)`, borderRadius:6,
            fontSize:12, fontWeight:700, fontFamily:T.font, cursor:'pointer', flexShrink:0 }}>
          🔄 Refresh
        </button>
      </div>

      {msg && (
        <div style={{ padding:'9px 14px', borderRadius:6, marginBottom:14, fontSize:12,
          background: msg.type==='ok' ? 'rgba(63,185,80,.1)' : 'rgba(248,81,73,.1)',
          border:`1px solid ${msg.type==='ok' ? 'rgba(63,185,80,.3)' : 'rgba(248,81,73,.25)'}`,
          color: msg.type==='ok' ? '#3fb950' : '#f85149' }}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ marginLeft:12, cursor:'pointer',
            background:'none', border:'none', color:'inherit', fontSize:14, lineHeight:1 }}>×</button>
        </div>
      )}

      {/* KPI bar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Open Cases',   value:openCount,      colour:'#f85149' },
          { label:'Under Review', value:reviewCount,    colour:'#E8A020' },
          { label:'Watchlist',    value:watchlistCount, colour:'#E8A020' },
          { label:'At Risk',      value:atRiskCount,    colour:'#f85149' },
        ].map(({ label, value, colour }) => (
          <div key={label} style={{ background:T.surface, borderRadius:10,
            border:`1px solid ${T.border}`, padding:'16px 20px' }}>
            <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, textTransform:'uppercase',
              letterSpacing:'.07em', marginBottom:8 }}>{label}</div>
            <div style={{ fontSize:28, fontWeight:800, lineHeight:1, color:colour }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select style={selS} value={filterWeek}   onChange={e => setFilterWeek(e.target.value)}>
          {weeks[0] && <option value={weeks[0]}>{weeks[0]} (Current)</option>}
          {weeks.slice(1).map(w => <option key={w} value={w}>{w}</option>)}
          <option value="">All Weeks</option>
        </select>
        <select style={selS} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="under_review">Under Review</option>
          <option value="closed">Closed</option>
        </select>
        <select style={selS} value={filterSev}    onChange={e => setFilterSev(e.target.value)}>
          <option value="">All Severity</option>
          <option value="watchlist">Watchlist</option>
          <option value="at_risk">At Risk</option>
        </select>
        <span style={{ fontSize:11, color:T.txtMuted, marginLeft:'auto' }}>
          {filtered.length} case{filtered.length!==1?'s':''} shown
        </span>
      </div>

      <div style={card}>
        <table style={tbl}>
          <thead>
            <tr>
              {['Super Agent','LGA','Tier','Week Ending','% Funded','Total Funded','Weekly Target','Severity','Status',''].map(h =>
                <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const rag      = enfRag(c.funding_pct)
              const sb       = statusBadge(c.status)
              const isExpand = expanded === c.id
              const noteList = notes[c.id] || []
              const hasNotes = noteList.length > 0
              // Notes locked once submitted for approval
              const notesLocked = c.status === 'under_review' || c.status === 'closed'

              return (
                <React.Fragment key={c.id}>
                  <tr style={{ cursor:'pointer', background: isExpand ? T.surface3 : 'transparent' }}
                    onClick={() => toggleExpand(c.id)}>
                    <td style={{ ...td, fontWeight:600 }}>{c.super_agents?.company_name || '—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{c.super_agents?.lgas?.name || '—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{c.super_agents?.lgas?.tier || '—'}</td>
                    <td style={{ ...td, color:T.txtSecondary, fontFamily:'monospace', fontSize:11 }}>{c.week_ending}</td>
                    <td style={td}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <div style={{ width:60, height:5, background:T.surface3, borderRadius:3, overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${Math.min(c.funding_pct,100)}%`,
                            background:rag.color, borderRadius:3 }} />
                        </div>
                        <span style={{ fontSize:12, fontWeight:700, color:rag.color }}>{c.funding_pct}%</span>
                      </div>
                    </td>
                    <td style={{ ...td, fontWeight:700, color:rag.color }}>{fmt(c.total_funded)}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{fmt(c.weekly_target)}</td>
                    <td style={td}>
                      <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20,
                        fontSize:9, fontWeight:700, letterSpacing:'.04em',
                        background:rag.bg, color:rag.color, border:`1px solid ${rag.border}` }}>
                        {rag.label}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20,
                        fontSize:9, fontWeight:700, letterSpacing:'.04em',
                        background:sb.bg, color:sb.color, border:`1px solid ${sb.border}` }}>
                        {sb.label}
                      </span>
                    </td>
                    <td style={{ ...td, color:T.txtMuted, fontSize:12, textAlign:'right' }}>
                      {isExpand ? '▲' : '▼'}
                    </td>
                  </tr>

                  {isExpand && (
                    <tr>
                      <td colSpan={10} style={{ padding:0, background:T.surface3,
                        borderBottom:`1px solid ${T.border}` }}>
                        <div style={{ padding:'20px 24px' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

                            {/* ── Left: Notes thread ── */}
                            <div>
                              <div style={{ fontSize:12, fontWeight:700, color:T.txtPrimary, marginBottom:12 }}>
                                Investigation Notes
                              </div>
                              <div style={{ maxHeight:220, overflowY:'auto', marginBottom:12,
                                border:`1px solid ${T.border}`, borderRadius:6, background:T.surface }}>
                                {noteList.length === 0 ? (
                                  <div style={{ padding:'16px', fontSize:11, color:T.txtMuted, textAlign:'center' }}>
                                    No notes yet.
                                  </div>
                                ) : noteList.map(n => (
                                  <div key={n.id} style={{ padding:'10px 14px',
                                    borderBottom:`1px solid ${T.border}` }}>
                                    <div style={{ fontSize:11, color:T.txtPrimary, lineHeight:1.5 }}>{n.note}</div>
                                    <div style={{ fontSize:10, color:T.txtMuted, marginTop:4 }}>
                                      {n.created_by_name || n.created_by_email || 'System'} · {fmtDT(n.created_at)}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Add note — enforcement only, only while Open */}
                              {canEdit && !notesLocked && (
                                <div>
                                  <div style={{ display:'flex', gap:8 }}>
                                    <textarea
                                      placeholder="Add investigation note…"
                                      rows={2}
                                      value={newNote[c.id] || ''}
                                      onChange={e => setNewNote(prev => ({ ...prev, [c.id]: e.target.value }))}
                                      style={{ flex:1, padding:'8px 10px', background:T.surface,
                                        border:`1px solid ${T.border}`, borderRadius:6, fontSize:11,
                                        fontFamily:T.font, color:T.txtPrimary, resize:'vertical' }}
                                    />
                                    <button
                                      disabled={saving[`note_${c.id}`] || !(newNote[c.id]||'').trim()}
                                      onClick={() => addNote(c.id)}
                                      style={{ padding:'8px 14px', background:'rgba(88,166,255,.15)',
                                        color:'#58a6ff', border:'1px solid rgba(88,166,255,.3)',
                                        borderRadius:6, fontSize:11, fontWeight:700,
                                        fontFamily:T.font, cursor:'pointer',
                                        opacity:saving[`note_${c.id}`] ? 0.6 : 1 }}>
                                      {saving[`note_${c.id}`] ? '…' : '+ Note'}
                                    </button>
                                  </div>

                                  {/* Submit for Approval */}
                                  {role === 'compliance_officer' && c.status === 'open' && (
                                    <div style={{ marginTop:10 }}>
                                      {!hasNotes && (
                                        <div style={{ fontSize:10, color:T.txtMuted, marginBottom:6,
                                          padding:'5px 10px', borderRadius:5,
                                          background:T.surface, border:`1px solid ${T.border}` }}>
                                          Add at least one note before submitting for approval.
                                        </div>
                                      )}
                                      <button
                                        disabled={saving[`submit_${c.id}`] || !hasNotes}
                                        onClick={() => submitForApproval(c.id)}
                                        style={{ width:'100%', padding:'9px 14px',
                                          background: hasNotes ? 'rgba(232,160,32,.15)' : T.surface3,
                                          color: hasNotes ? '#E8A020' : T.txtMuted,
                                          border:`1px solid ${hasNotes ? 'rgba(232,160,32,.3)' : T.border}`,
                                          borderRadius:6, fontSize:12, fontWeight:700,
                                          fontFamily:T.font, cursor: hasNotes ? 'pointer' : 'not-allowed',
                                          opacity:saving[`submit_${c.id}`] ? 0.6 : 1 }}>
                                        {saving[`submit_${c.id}`] ? '…' : '⬆ Submit for Approval'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Locked notice for enforcement after submission */}
                              {canEdit && !canClose && notesLocked && (
                                <div style={{ padding:'10px 14px', borderRadius:6, fontSize:11,
                                  background:T.surface, border:`1px solid ${T.border}`, color:T.txtMuted,
                                  textAlign:'center' }}>
                                  {c.status === 'under_review'
                                    ? '🔒 Case submitted for approval — investigation notes are locked'
                                    : '🔒 Case closed — notes are read-only'}
                                </div>
                              )}
                            </div>

                            {/* ── Right: Case Actions panel ── */}
                            <div>
                              <div style={{ fontSize:12, fontWeight:700, color:T.txtPrimary, marginBottom:12 }}>
                                Case Actions
                              </div>

                              {/* Audit trail */}
                              <div style={{ fontSize:11, lineHeight:2, background:T.surface,
                                border:`1px solid ${T.border}`, borderRadius:6,
                                padding:'10px 14px', marginBottom:12 }}>
                                <div>
                                  <strong style={{ color:T.txtPrimary }}>Opened:</strong>{' '}
                                  <span style={{ color:T.txtSecondary }}>{fmtD(c.created_at)}</span>
                                </div>
                                <div>
                                  <strong style={{ color:T.txtPrimary }}>Week:</strong>{' '}
                                  <span style={{ color:T.txtSecondary }}>{c.week_ending}</span>
                                </div>
                                <div>
                                  <strong style={{ color:T.txtPrimary }}>Funding:</strong>{' '}
                                  <span style={{ color:T.txtSecondary }}>
                                    {fmt(c.total_funded)} of {fmt(c.weekly_target)} ({c.funding_pct}%)
                                  </span>
                                </div>

                                {/* Enforcement officer trail */}
                                {c.reviewed_by_email && (
                                  <>
                                    <div style={{ marginTop:6, paddingTop:6, borderTop:`1px solid ${T.border}` }}>
                                      <strong style={{ color:'#E8A020' }}>Treated by:</strong>{' '}
                                      <span style={{ color:T.txtSecondary }}>{c.reviewed_by_name || c.reviewed_by_email}</span>
                                    </div>
                                    <div>
                                      <strong style={{ color:'#E8A020' }}>Treated on:</strong>{' '}
                                      <span style={{ color:T.txtSecondary }}>{fmtDT(c.reviewed_at)}</span>
                                    </div>
                                  </>
                                )}

                                {/* Admin close trail */}
                                {c.status === 'closed' && (
                                  <>
                                    <div style={{ marginTop:6, paddingTop:6, borderTop:`1px solid ${T.border}` }}>
                                      <strong style={{ color:'#3fb950' }}>Closed by:</strong>{' '}
                                      <span style={{ color:T.txtSecondary }}>{c.closed_by_name || c.closed_by_email || '—'}</span>
                                    </div>
                                    <div>
                                      <strong style={{ color:'#3fb950' }}>Closed on:</strong>{' '}
                                      <span style={{ color:T.txtSecondary }}>{fmtDT(c.closed_at)}</span>
                                    </div>
                                    {c.closing_note && (
                                      <div style={{ marginTop:4 }}>
                                        <strong style={{ color:T.txtPrimary }}>Closing note:</strong>{' '}
                                        <span style={{ color:T.txtSecondary }}>{c.closing_note}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>

                              {/* Admin: awaiting submission */}
                              {canClose && c.status === 'open' && (
                                <div style={{ padding:'12px 14px', background:T.surface3,
                                  border:`1px solid ${T.border}`, borderRadius:6,
                                  fontSize:11, color:T.txtMuted, textAlign:'center' }}>
                                  ⏳ Awaiting compliance officer submission
                                </div>
                              )}

                              {/* Admin close panel — only when Under Review */}
                              {canClose && c.status === 'under_review' && (
                                <div style={{ background:'rgba(63,185,80,.06)',
                                  border:'1px solid rgba(63,185,80,.2)', borderRadius:6, padding:'12px' }}>
                                  <div style={{ fontSize:11, color:'#3fb950', fontWeight:700, marginBottom:8 }}>
                                    Admin Sign-off — closing note required
                                  </div>
                                  <textarea
                                    placeholder="Closing note (required)…"
                                    rows={2}
                                    value={closingNote[c.id] || ''}
                                    onChange={e => setClosingNote(prev => ({ ...prev, [c.id]: e.target.value }))}
                                    style={{ width:'100%', padding:'8px 10px', background:T.surface,
                                      border:'1px solid rgba(63,185,80,.25)', borderRadius:6, fontSize:11,
                                      fontFamily:T.font, color:T.txtPrimary, resize:'vertical',
                                      marginBottom:8, boxSizing:'border-box' }}
                                  />
                                  <button
                                    disabled={saving[`close_${c.id}`] || !(closingNote[c.id]||'').trim()}
                                    onClick={() => closeCase(c.id)}
                                    style={{ width:'100%', padding:'9px 14px',
                                      background:'rgba(63,185,80,.15)', color:'#3fb950',
                                      border:'1px solid rgba(63,185,80,.3)', borderRadius:6,
                                      fontSize:12, fontWeight:700, fontFamily:T.font, cursor:'pointer',
                                      opacity:saving[`close_${c.id}`] || !(closingNote[c.id]||'').trim() ? 0.5 : 1 }}>
                                    {saving[`close_${c.id}`] ? '…' : '✓ Close Case'}
                                  </button>
                                </div>
                              )}

                              {/* Closed confirmation */}
                              {c.status === 'closed' && (
                                <div style={{ padding:'12px 14px', background:'rgba(63,185,80,.08)',
                                  border:'1px solid rgba(63,185,80,.25)', borderRadius:6,
                                  fontSize:11, color:'#3fb950', textAlign:'center', fontWeight:600 }}>
                                  ✓ Case closed
                                </div>
                              )}

                              {/* Guest / read-only */}
                              {!canEdit && c.status !== 'closed' && (
                                <div style={{ padding:'12px 14px', background:T.surface3,
                                  border:`1px solid ${T.border}`, borderRadius:6,
                                  fontSize:11, color:T.txtMuted, textAlign:'center' }}>
                                  🔒 View only
                                </div>
                              )}
                            </div>

                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} style={{ ...td, textAlign:'center', color:T.txtMuted, padding:32 }}>
                {cases.length === 0
                  ? 'No enforcement cases yet. Cases are auto-created when scoring is run for SAs below 50%.'
                  : 'No cases match the current filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
